/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ApolloClient, RefetchQueriesInclude } from "@apollo/client";

// With awaitRefetchQueries, Apollo rejects the mutation when a refetch fails, so a
// save that worked ends in the caller's error toast, and someone saves again. A
// refetch only updates what's on the screen, so here it runs after the mutation, the
// caller still waits for it, and a failure is reported instead of thrown.
export function settleRefetches(
  client: ApolloClient<any>,
  onFailedRefetch: (error: unknown, queryName: string | undefined) => void,
) {
  const { mutate } = client;

  client.mutate = (async (options: Parameters<typeof mutate>[0]) => {
    const { refetchQueries, awaitRefetchQueries, ...mutation } = options;
    if (!refetchQueries || !awaitRefetchQueries || options.onQueryUpdated) {
      return mutate(options);
    }

    const result = await mutate(mutation);

    const { queries, results } = client.refetchQueries({
      include: (typeof refetchQueries === "function"
        ? refetchQueries(result)
        : refetchQueries) as RefetchQueriesInclude,
    });
    const settled = await Promise.allSettled(results);
    settled.forEach((outcome, index) => {
      if (outcome.status === "rejected") {
        onFailedRefetch(outcome.reason, queries[index]?.queryName);
      }
    });

    return result;
  }) as typeof client.mutate;
}
